/**
 * Offline consistency checks for Indian business identifiers — browser copy.
 *
 * THE BACKEND IS AUTHORITATIVE. This exists purely so the form can tell a user their
 * GSTIN is mistyped without a round trip; every value is re-checked server-side in
 * src/modules/company/companyIdentifierChecks.ts before anything is saved.
 *
 * The backend is TypeScript and this is JavaScript, so the logic cannot be shared as a
 * module. identifierChecks.test.js therefore duplicates the backend's test vectors
 * verbatim — if the two implementations ever diverge, one of the suites fails.
 *
 * These checks prove well-formedness, not existence. A checksum-valid GSTIN is not a
 * verified GSTIN; never label their output "Verified".
 */

const GSTIN_CHARSET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_SHAPE_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/** GST state codes. 25 and 28 are retained for historical registrations. */
export const GST_STATE_CODES = {
  '01': 'Jammu and Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  10: 'Bihar',
  11: 'Sikkim',
  12: 'Arunachal Pradesh',
  13: 'Nagaland',
  14: 'Manipur',
  15: 'Mizoram',
  16: 'Tripura',
  17: 'Meghalaya',
  18: 'Assam',
  19: 'West Bengal',
  20: 'Jharkhand',
  21: 'Odisha',
  22: 'Chhattisgarh',
  23: 'Madhya Pradesh',
  24: 'Gujarat',
  25: 'Daman and Diu',
  26: 'Dadra and Nagar Haveli and Daman and Diu',
  27: 'Maharashtra',
  28: 'Andhra Pradesh',
  29: 'Karnataka',
  30: 'Goa',
  31: 'Lakshadweep',
  32: 'Kerala',
  33: 'Tamil Nadu',
  34: 'Puducherry',
  35: 'Andaman and Nicobar Islands',
  36: 'Telangana',
  37: 'Andhra Pradesh',
  38: 'Ladakh',
  97: 'Other Territory',
  99: 'Centre Jurisdiction'
};

/** PAN 4th character → holder category. */
export const PAN_HOLDER_TYPE_BY_CHAR = {
  A: 'Association of Persons',
  B: 'Body of Individuals',
  C: 'Company',
  F: 'Firm / LLP',
  G: 'Government',
  H: 'Hindu Undivided Family',
  J: 'Artificial Juridical Person',
  L: 'Local Authority',
  P: 'Individual / Proprietor',
  T: 'Trust'
};

/**
 * Expected PAN 4th characters per entity type. Societies are deliberately permissive —
 * they register as AOP or Trust depending on the state Act, so a strict rule would
 * reject valid customers.
 */
export const EXPECTED_PAN_CHARS = {
  'Private Limited': ['C'],
  'Public Limited': ['C'],
  LLP: ['F'],
  Partnership: ['F'],
  Proprietorship: ['P'],
  Trust: ['T'],
  Society: ['A', 'T', 'B'],
  HUF: ['H']
};

function normalizeId(value) {
  return (value ?? '').toUpperCase().replace(/\s/g, '');
}

export function isValidPanShape(pan) {
  return PAN_REGEX.test(normalizeId(pan));
}

export function isValidGstinShape(gstin) {
  return GSTIN_SHAPE_REGEX.test(normalizeId(gstin));
}

/**
 * GSTIN check digit: mod-36 over the first 14 characters. Weight alternates 1,2 starting
 * at 1; each product contributes floor(p/36) + (p % 36).
 */
export function computeGstinCheckChar(first14) {
  const value = normalizeId(first14);
  if (value.length !== 14) return null;

  let sum = 0;
  for (let i = 0; i < 14; i += 1) {
    const digit = GSTIN_CHARSET.indexOf(value[i]);
    if (digit < 0) return null;
    const product = digit * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(product / GSTIN_CHARSET.length) + (product % GSTIN_CHARSET.length);
  }

  const checkIndex = (GSTIN_CHARSET.length - (sum % GSTIN_CHARSET.length)) % GSTIN_CHARSET.length;
  return GSTIN_CHARSET[checkIndex];
}

export function isValidGstinChecksum(gstin) {
  const value = normalizeId(gstin);
  if (value.length !== 15) return false;
  const expected = computeGstinCheckChar(value.slice(0, 14));
  return expected !== null && expected === value[14];
}

/** GSTIN characters 3–12 are the PAN of the registered entity. */
export function panFromGstin(gstin) {
  const value = normalizeId(gstin);
  if (value.length !== 15) return null;
  return value.slice(2, 12);
}

export function gstinStateName(gstin) {
  const value = normalizeId(gstin);
  if (value.length < 2) return null;
  return GST_STATE_CODES[value.slice(0, 2)] ?? null;
}

export function panHolderTypeChar(pan) {
  const value = normalizeId(pan);
  if (!PAN_REGEX.test(value)) return null;
  return value[3];
}

/**
 * Cross-field warnings for the form. Errors (mistyped GSTIN, PAN mismatch) are raised by
 * the yup schema; these are the advisory ones that must never block a save — a business
 * can legitimately hold GST registration outside its head-office state.
 */
/**
 * The state field starts as the placeholder '-' and becomes real only after the pincode
 * lookup runs. Comparing against a placeholder produced "the address state is -", which
 * is noise, not a warning.
 */
function hasRealState(state) {
  const value = String(state ?? '').trim();
  if (!value) return false;
  return !['-', '--', 'select', 'n/a'].includes(value.toLowerCase());
}

export function collectIdentifierWarnings({ gstin, state, pan, entityType }) {
  const warnings = [];
  const cleanGstin = normalizeId(gstin);
  const cleanPan = normalizeId(pan);

  if (isValidGstinShape(cleanGstin) && hasRealState(state)) {
    const gstState = gstinStateName(cleanGstin);
    if (gstState && gstState.toUpperCase() !== String(state).toUpperCase()) {
      warnings.push(
        `GSTIN is registered in ${gstState} but the address state is ${state}. That is legal — confirm it is intentional.`
      );
    }
  }

  if (isValidPanShape(cleanPan) && entityType) {
    const actual = panHolderTypeChar(cleanPan);
    const expected = EXPECTED_PAN_CHARS[entityType] ?? [];
    if (actual && expected.length && !expected.includes(actual) && !PAN_HOLDER_TYPE_BY_CHAR[actual]) {
      warnings.push(`PAN holder-type character "${actual}" is not recognised — confirm the PAN.`);
    }
  }

  return warnings;
}
